import { router } from '@inertiajs/react'
import type { ReactNode } from 'react'

type RewardStandby = {
  hours: number
  hourly_rate: number
  amount: number
}

type RewardExaminations = {
  normal_count: number
  suggest_canceled_count: number
  late_canceled_count: number
  medical_document_count: number
  adjustment_delta: number
  reward_target_count: number
  unit_rate: number
  late_cancel_unit_price: number
  amount: number
}

type RewardConsultations = {
  count: number
  adjustment_delta: number
  reward_target_count: number
  unit_price: number
  amount: number
}

type FixedAllowance = {
  note: string | null
  amount: number
  min_standby_hours: number | null
  granted: boolean
}

type Reward = {
  year_month: string
  rate_plan_name: string
  rank_name: string | null
  standby: RewardStandby
  examinations: RewardExaminations
  consultations: RewardConsultations
  fixed_allowances: FixedAllowance[]
  fixed_allowance_amount: number
  manual_allowance_amount: number
  deduction_amount: number
  total: number
}

type RankUp = {
  hours_to_next: number
  next_rank_name: string
  next_standby_hourly_rate: number
  next_examination_unit_rate: number
}

type RankTier = {
  rank_name: string
  min_standby_hours: number
  max_standby_hours: number | null
  standby_hourly_rate: number
  examination_unit_rate: number
}

type ConsultationTier = {
  min_monthly_count: number
  unit_price: number
}

type RateTable = {
  rank_tiers: RankTier[]
  consultation_tiers: ConsultationTier[]
}

export type RewardSectionProps = {
  selected_month: string
  available_months: string[]
  reward: Reward
  confirmed: boolean
  rank_up: RankUp | null
  rate_table: RateTable
}

function yen(amount: number): string {
  return `${amount.toLocaleString('ja-JP')}円`
}

function monthLabel(yearMonth: string): string {
  const [year, month] = yearMonth.split('-')
  return `${year}年${Number(month)}月`
}

/** 「12.25h」のような短い時間表記（端数がなければ整数で） */
function hoursLabel(hours: number): string {
  return `${Number.isInteger(hours) ? hours : hours.toFixed(2)}h`
}

/** 時間を15分枠の本数に換算した表記（「83枠」）。医師の感覚は枠数なので併記する */
function slotCountLabel(hours: number): string {
  return `${Math.round(hours * 4)}枠`
}

function SubSection({ title, children, className = '' }: { title: string; children: ReactNode; className?: string }) {
  return (
    <div className={`flex flex-col ${className}`}>
      <h3 className="text-sm font-semibold text-[#1a1f36] mb-1.5">{title}</h3>
      {children}
    </div>
  )
}

// 稼働3カード共通の器。上部中央に金額、その下に計算式、さらに下に内訳（任意）
function AmountCard({ amount, formula, children }: { amount: string; formula: string; children?: ReactNode }) {
  return (
    <div className="flex-1 bg-white border border-gray-200 rounded-lg p-4">
      <div className="text-center">
        <div className="text-xl font-bold text-[#1a1f36]">{amount}</div>
        <div className="text-xs text-gray-500 mt-0.5">{formula}</div>
      </div>
      {children && <div className="mt-3 pt-3 border-t border-gray-100 space-y-1">{children}</div>}
    </div>
  )
}

// 内訳の1行（区分名と件数）
function BreakdownLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center text-xs text-gray-600">
      <span>{label}</span>
      <span className="ml-auto whitespace-nowrap">{value}</span>
    </div>
  )
}

// 計算式（detail）は下段に分けて表示する。狭いカラムで「× 1,250円/h」が
// 変な位置で折り返さないようにするため
function BreakdownRow({ label, detail, amount, muted }: { label: string; detail?: string; amount?: string; muted?: boolean }) {
  return (
    <div className={`px-4 py-2.5 ${muted ? 'text-gray-400' : 'text-gray-700'}`}>
      <div className="flex items-center gap-3">
        <span className="text-sm">{label}</span>
        {amount && <span className="text-sm font-semibold ml-auto whitespace-nowrap">{amount}</span>}
      </div>
      {detail && <div className="text-xs text-gray-500 mt-0.5 whitespace-nowrap">{detail}</div>}
    </div>
  )
}

/** 待機時間帯のラベル（「20〜25h未満」「50h以上」） */
function standbyRangeLabel(tier: RankTier): string {
  if (tier.max_standby_hours === null) return `${tier.min_standby_hours}h以上`
  if (tier.min_standby_hours === 0) return `${tier.max_standby_hours}h未満`
  return `${tier.min_standby_hours}〜${tier.max_standby_hours}h未満`
}

/** 制度説明（ランク表・相談単価表）。単価マスタから生成し、現在の適用行をハイライトする */
function RateTableDetails({ rateTable, standbyHours, consultationCount }: {
  rateTable: RateTable
  standbyHours: number
  consultationCount: number
}) {
  // 「条件を満たす最後の行」= 現在適用されている単価帯
  const currentRankIndex = rateTable.rank_tiers.reduce(
    (found, tier, i) => (Math.floor(standbyHours) >= tier.min_standby_hours ? i : found), -1,
  )
  const currentConsultationIndex = rateTable.consultation_tiers.reduce(
    (found, tier, i) => (consultationCount >= tier.min_monthly_count ? i : found), -1,
  )
  const thClass = 'text-left text-xs text-gray-500 font-normal px-3 py-1.5 border-b border-gray-200'
  const tdClass = 'px-3 py-1.5 border-b border-gray-100'

  return (
    <details>
      <summary className="cursor-pointer text-sm text-blue-600 hover:underline">
        報酬制度について（ランク表・相談単価）
      </summary>
      <p className="text-xs text-gray-500 mt-2">
        月間の待機枠登録時間でランクが決まり、待機単価と診療単価の両方が変わります。相談・サプリ相談は当月の対応件数で単価が変わります。
        現在の適用行を色付きで表示しています。
      </p>
      <div className="mt-2 grid gap-4 lg:grid-cols-2 items-start">
        <table className="w-full text-sm bg-white border border-gray-200 rounded-lg border-separate border-spacing-0">
          <thead>
            <tr>
              <th className={thClass}>ランク</th>
              <th className={thClass}>月間待機時間</th>
              <th className={thClass}>待機単価</th>
              <th className={thClass}>診療単価</th>
            </tr>
          </thead>
          <tbody>
            {rateTable.rank_tiers.map((tier, i) => (
              <tr key={i} className={i === currentRankIndex ? 'bg-blue-50 font-medium' : ''}>
                <td className={tdClass}>{tier.rank_name}</td>
                <td className={tdClass}>{standbyRangeLabel(tier)}</td>
                <td className={tdClass}>{yen(tier.standby_hourly_rate)}/h</td>
                <td className={tdClass}>{yen(tier.examination_unit_rate)}/件</td>
              </tr>
            ))}
          </tbody>
        </table>
        <table className="w-full text-sm bg-white border border-gray-200 rounded-lg border-separate border-spacing-0">
          <thead>
            <tr>
              <th className={thClass}>当月の相談対応件数</th>
              <th className={thClass}>相談単価</th>
            </tr>
          </thead>
          <tbody>
            {rateTable.consultation_tiers.map((tier, i) => (
              <tr key={i} className={i === currentConsultationIndex ? 'bg-blue-50 font-medium' : ''}>
                <td className={tdClass}>
                  {tier.min_monthly_count === 0
                    ? `${rateTable.consultation_tiers[1]?.min_monthly_count ?? '-'}件未満`
                    : `${tier.min_monthly_count}件以上`}
                </td>
                <td className={tdClass}>{yen(tier.unit_price)}/件</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  )
}

/**
 * 報酬の内訳表示 (#1096)。ダッシュボードホーム内に月選択つきで全部出す（専用ページなし）。
 * 診療件数は区分別内訳と報酬対象合計を分けて見せる（診療実施の件数を水増しする表示にはしない）。
 */
export default function RewardSection({
  section, homeHref, scheduleHref, extraParams = {},
}: { section: RewardSectionProps; homeHref: string; scheduleHref: string; extraParams?: Record<string, string> }) {
  const { selected_month, available_months, reward, confirmed, rank_up, rate_table } = section

  const changeMonth = (month: string) => {
    // ホームの他セクションの選択状態（日付ナビ等）を打ち消さないようパラメータを持ち回る
    router.get(homeHref, { ...extraParams, reward_month: month }, { preserveScroll: true })
  }

  return (
    <section className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 sm:p-6 mb-8">
      {/* ホームのグループカード（要対応/診察スケジュール）とスタイルを揃える */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="flex items-center gap-2.5 text-xl font-bold text-[#1a1f36]">
          <span className="w-1.5 self-stretch rounded-sm bg-[#2196F3]" aria-hidden="true" />
          報酬
        </h2>
        <select
          value={selected_month}
          onChange={(e) => changeMonth(e.target.value)}
          className="border border-gray-300 rounded-lg text-sm px-3 py-1.5 bg-white"
        >
          {available_months.map((month) => (
            <option key={month} value={month}>{monthLabel(month)}</option>
          ))}
        </select>
      </div>

      {/* ランクアップナビ（枠開放の動機づけなのでカードの一番上に置く） */}
      {rank_up && (
        <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 mb-4 text-sm text-green-800">
          今月の待機 {hoursLabel(reward.standby.hours)}（{slotCountLabel(reward.standby.hours)}） →
          あと{hoursLabel(rank_up.hours_to_next)}（{slotCountLabel(rank_up.hours_to_next)}）で
          {rank_up.next_rank_name !== reward.rank_name && 'ランクが上がり、'}
          {rank_up.next_standby_hourly_rate !== reward.standby.hourly_rate
            ? '待機単価/診療単価が上がります。'
            : '診療単価が上がります。'}
          <a href={scheduleHref} className="text-green-700 underline ml-1 whitespace-nowrap">
            医師スケジュールで枠を開放する
          </a>
        </div>
      )}

      {/* 見込み合計（確定済みの月はスナップショットの確定額） */}
      <div className="bg-white border border-gray-200 rounded-lg px-5 py-4 mb-4">
        <div className="flex items-baseline gap-3 flex-wrap">
          <span className="text-sm text-gray-500">
            {monthLabel(reward.year_month)}の{confirmed ? '報酬額' : '見込み報酬額'}
          </span>
          <span className="text-2xl font-bold text-[#1a1f36]">{yen(reward.total)}</span>
          {reward.rank_name && (
            <span className="text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5">
              ランク: {reward.rank_name}
            </span>
          )}
          {confirmed && (
            <span className="text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">
              確定済み
            </span>
          )}
        </div>
        <p className="text-xs text-gray-400 mt-2">
          {confirmed
            ? 'この月の報酬は確定済みです。'
            : '確定前のリアルタイム集計です。実際の支払額は月次確定後の金額が優先されます。'}
        </p>
      </div>

      {/* 3つの稼働内訳は横並び（モバイルは縦積み）。各カードとも上部中央に金額→式→内訳の順 */}
      <div className="grid gap-4 lg:grid-cols-3 mb-4">
        <SubSection title="待機報酬" className="h-full">
          <AmountCard
            amount={yen(reward.standby.amount)}
            formula={`${hoursLabel(reward.standby.hours)}（${slotCountLabel(reward.standby.hours)}） × ${yen(reward.standby.hourly_rate)}/h`}
          />
        </SubSection>

        {/* 診療報酬（金額+式の下に区分別内訳） */}
        <SubSection title="診療報酬" className="h-full">
          <AmountCard
            amount={yen(reward.examinations.amount)}
            formula={`報酬対象 計${reward.examinations.reward_target_count}件 × ${yen(reward.examinations.unit_rate)}/件`}
          >
            <BreakdownLine label="診療実施" value={`${reward.examinations.normal_count}件`} />
            {reward.examinations.medical_document_count > 0 && (
              <BreakdownLine label="診断書・紹介状" value={`${reward.examinations.medical_document_count}件`} />
            )}
            {reward.examinations.suggest_canceled_count > 0 && (
              <BreakdownLine label="処方提案後キャンセル" value={`${reward.examinations.suggest_canceled_count}件`} />
            )}
            {reward.examinations.late_canceled_count > 0 && (
              <BreakdownLine
                label={`キャンセル料徴収分（${yen(reward.examinations.late_cancel_unit_price)}/件）`}
                value={`${reward.examinations.late_canceled_count}件`}
              />
            )}
            {reward.examinations.adjustment_delta !== 0 && (
              <BreakdownLine
                label="件数補正"
                value={`${reward.examinations.adjustment_delta > 0 ? '+' : ''}${reward.examinations.adjustment_delta}件`}
              />
            )}
          </AmountCard>
        </SubSection>

        <SubSection title="相談・サプリ相談報酬" className="h-full">
          <AmountCard
            amount={yen(reward.consultations.amount)}
            formula={`報酬対象 計${reward.consultations.reward_target_count}件 × ${yen(reward.consultations.unit_price)}/件`}
          >
            {reward.consultations.adjustment_delta !== 0 && (
              <BreakdownLine
                label={`対応 ${reward.consultations.count}件・件数補正`}
                value={`${reward.consultations.adjustment_delta > 0 ? '+' : ''}${reward.consultations.adjustment_delta}件`}
              />
            )}
            <p className="text-xs text-gray-400">当月の対応件数で単価が変わります</p>
          </AmountCard>
        </SubSection>
      </div>

      {reward.fixed_allowances.length > 0 && (
        <SubSection title="固定報酬" className="mb-4">
          <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
            {reward.fixed_allowances.map((allowance, i) => (
              <BreakdownRow
                key={i}
                label={allowance.note || '固定報酬'}
                detail={
                  allowance.granted
                    ? undefined
                    : `条件未達（月間待機${allowance.min_standby_hours}h以上で支給）`
                }
                amount={allowance.granted ? yen(allowance.amount) : yen(0)}
                muted={!allowance.granted}
              />
            ))}
          </div>
        </SubSection>
      )}

      {(reward.manual_allowance_amount > 0 || reward.deduction_amount > 0) && (
        <SubSection title="調整" className="mb-4">
          <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
            {reward.manual_allowance_amount > 0 && (
              <BreakdownRow label="手当" amount={yen(reward.manual_allowance_amount)} />
            )}
            {reward.deduction_amount > 0 && (
              <BreakdownRow label="控除" amount={`-${yen(reward.deduction_amount)}`} />
            )}
          </div>
        </SubSection>
      )}

      <RateTableDetails
        rateTable={rate_table}
        standbyHours={reward.standby.hours}
        consultationCount={reward.consultations.reward_target_count}
      />
    </section>
  )
}
